import images from '@assets/images/index'
import { Button, DynamicSvgIcon } from '@components/ui'
import { useQuery } from '@tanstack/react-query'
import { Badge, Flex, Progress, Steps } from 'antd'
import { memo, useCallback, useMemo, useState } from 'react'

import { Link, useLocation } from '@suntex.core/nav'
import {
  Database_Procs_GetPlayerQuest_Item,
  Entities_Player_Quests_Response
} from '@suntex.fim/api-client'
import { BadgeSlug, QuestStatus } from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { isValidPlayerId, usePlayer } from '@suntex.fim/player'
import { LoaderOverlay, Modal } from '@suntex.fim/ui'

import {
  BADGE_ITEMS,
  BadgeStats,
  getBadgeProgressPercentage,
  getCurrentBadgeLevel,
  getCurrentStepIndex,
  getStickersToNextLevel
} from '../../utils/badgeUtils'

// Memoize component to prevent unnecessary re-renders
const MyBadges = memo(() => {
  const { api } = useFramework()
  const { playerId } = usePlayer()
  // State management
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Memoized event handlers to prevent unnecessary re-renders
  const handleShowModal = useCallback(() => {
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const { data: myBadges = {}, isLoading: isBadgeLoading } = useQuery<
    Partial<Entities_Player_Quests_Response>
  >({
    queryKey: ['player', 'badges', playerId],
    queryFn: async () => {
      const badgesResponse = await api.get_Player_Quests({
        playerId: playerId,
        includeCompletedQuests: true
      })
      return badgesResponse
    },
    enabled: isValidPlayerId(playerId)
  })

  // Memoized calculations - only recalculate when stickers change
  const stickerCount = useMemo(
    () => myBadges.stickers ?? 0,
    [myBadges.stickers]
  )

  const badgeStats = useMemo<BadgeStats>(() => {
    const earnedToday = stickerCount - (myBadges.stickers1D ?? 0)
    const earnedThisWeek = stickerCount - (myBadges.stickers1W ?? 0)
    const stickersToNext = getStickersToNextLevel(stickerCount)
    const progressPercentage = getBadgeProgressPercentage(stickerCount)
    return {
      earnedToday,
      earnedThisWeek,
      stickersToNext,
      progressPercentage
    }
  }, [stickerCount, myBadges.stickers1D, myBadges.stickers1W])

  // Get current badge level information
  const currentBadgeLevel = useMemo(() => {
    return getCurrentBadgeLevel(stickerCount)
  }, [stickerCount])

  // Helper function to safely render badge title
  const renderBadgeTitle = useCallback((title: React.ReactNode): string => {
    return typeof title === 'string' ? title : 'Grand Champion'
  }, [])

  /**
   * Generic badge renderer for reusability and performance
   * Handles completed, active, and locked badge states consistently
   */
  const renderBadge = useCallback(
    (
      badge: Database_Procs_GetPlayerQuest_Item,
      ActiveIcon: React.ComponentType,
      LockedIcon: React.ComponentType
    ) => {
      switch (badge.status) {
        case QuestStatus.Completed:
          return (
            <div key={badge.id}>
              <Flex
                justify="center"
                align="center"
                className="my-award-card__icon"
                role="img"
                aria-label={`Completed badge: ${badge.questName || 'Unknown badge'}`}
              >
                <DynamicSvgIcon quest={badge} />
              </Flex>
            </div>
          )
        case QuestStatus.Active:
          return (
            <div key={badge.id} className="active-badge-wrapper">
              <ActiveIcon />
              <p className="active-badge">In-Progress</p>
            </div>
          )
        default:
          return (
            <Flex
              justify="center"
              align="center"
              className="badge-quest"
              key={badge.id}
              role="button"
              tabIndex={0}
              aria-label={`Locked badge: ${badge.questName || 'Unknown badge'}`}
            >
              <Flex
                justify="center"
                align="center"
                className="badge-quest-name"
              >
                {badge.questName}
              </Flex>
              <LockedIcon />
            </Flex>
          )
      }
    },
    []
  )

  /**
   * Renders badge with count for repeatable badges
   */
  const renderBadgeWithCount = useCallback(
    (
      badge: Database_Procs_GetPlayerQuest_Item,
      ActiveIcon: React.ComponentType,
      LockedIcon: React.ComponentType
    ) => {
      if (badge.status === QuestStatus.Completed) {
        return (
          <Flex
            justify="center"
            align="center"
            key={badge.id}
            className="badge-container"
          >
            <Badge
              count={badge.timesCompleted || 0}
              aria-label={`Completed ${badge.timesCompleted || 0} times`}
            >
              <Flex
                justify="center"
                align="center"
                className="my-award-card__icon"
                role="img"
                aria-label={`Completed badge: ${badge.questName || 'Unknown badge'}`}
              >
                <DynamicSvgIcon quest={badge} />
              </Flex>
            </Badge>
          </Flex>
        )
      }
      return renderBadge(badge, ActiveIcon, LockedIcon)
    },
    [renderBadge]
  )

  /**
   * Memoized badge filtering functions for better performance
   */
  const filteredBadges = useMemo(() => {
    const questItems = myBadges.questItems || []

    return {
      stickerBadges: questItems.filter(
        (badge) =>
          badge.slug?.includes(BadgeSlug.Stickers) &&
          [
            BadgeSlug.Stickers1000,
            BadgeSlug.Stickers2000,
            BadgeSlug.Stickers3000,
            BadgeSlug.Stickers4000,
            BadgeSlug.Stickers5000,
            BadgeSlug.Stickers6000,
            BadgeSlug.Stickers8000,
            BadgeSlug.Stickers10000
          ].some((num) => badge.slug?.includes(num))
      ),
      skillSetBadges: questItems.filter((badge) =>
        badge.slug?.includes(BadgeSlug.CompleteSs)
      ),
      jtfBadges: questItems.filter((badge) =>
        badge.slug?.includes(BadgeSlug.CompleteJtf)
      ),
      gymAddSubBadges: questItems.filter(
        (badge) =>
          badge.slug?.includes(BadgeSlug.CompleteGym) &&
          (badge.slug.includes(BadgeSlug.GymAdd) ||
            badge.slug.includes(BadgeSlug.GymSub))
      ),
      gymMulDivBadges: questItems.filter(
        (badge) =>
          badge.slug?.includes(BadgeSlug.CompleteGym) &&
          (badge.slug.includes(BadgeSlug.GymMul) ||
            badge.slug.includes(BadgeSlug.GymDiv))
      ),
      kasMilestoneBadges: questItems.filter(
        (badge) =>
          badge.slug?.includes(BadgeSlug.Kas) ||
          badge.slug?.includes(BadgeSlug.Milestone)
      ),
      mwGameBadges: questItems.filter((badge) =>
        badge.slug?.includes(BadgeSlug.CompleteMwGame)
      ),
      bonusBadges: questItems.filter((badge) =>
        badge.slug?.includes(BadgeSlug.CompleteBonus)
      ),
      // Bonus badge categories
      potdBadges: questItems.filter((badge) =>
        badge.slug?.includes(BadgeSlug.WinPotd)
      ),
      topTenBadges: questItems.filter((badge) =>
        badge.slug?.includes(BadgeSlug.TopTen)
      ),
      weeklySticker200Badges: questItems.filter((badge) =>
        badge.slug?.includes(BadgeSlug.Stickers200Week)
      ),
      ctGameBadges: questItems.filter((badge) =>
        badge.slug?.includes(BadgeSlug.CompleteCt)
      ),
      epicGameBadges: questItems.filter((badge) =>
        badge.slug?.includes(BadgeSlug.TryRpcEpic)
      )
    }
  }, [myBadges.questItems])

  // Loading state with comprehensive error handling
  if (isBadgeLoading) {
    return (
      <div className="my-badge-loading-container" aria-live="polite">
        <LoaderOverlay isLoading={isBadgeLoading} />
      </div>
    )
  }

  // Error state handling for better UX
  if (Object.keys(myBadges).length === 0) {
    return (
      <div className="my-award-container" role="main" aria-label="My Badges">
        <Flex vertical gap={40}>
          <Flex justify="space-between" wrap gap={10}>
            <h3>Check out your amazing progress!</h3>
            <Link
              to="/player/my-quests"
              style={{ textDecoration: 'none' }}
              aria-label="Navigate to My Quests page"
            >
              <Button intent="primary">Go to My Quests</Button>
            </Link>
          </Flex>
          <div className="no-badges-message" role="status">
            <p>No badge data available. Please try refreshing the page.</p>
          </div>
        </Flex>
      </div>
    )
  }

  // Error boundary fallback for runtime errors
  const ErrorFallback = ({ error }: { error: Error }) => (
    <div className="my-award-container" role="alert" aria-live="assertive">
      <Flex vertical gap={20} align="center" style={{ padding: '40px' }}>
        <h3>Something went wrong while loading your badges</h3>
        <p>
          We apologize for the inconvenience. Please refresh the page or try
          again later.
        </p>
        <Button onClick={() => window.location.reload()}>Refresh Page</Button>
      </Flex>
    </div>
  )

  return (
    <Flex
      vertical
      gap={40}
      className="my-award-container"
      role="main"
      aria-label="My Badges Dashboard"
    >
      <Flex justify="space-between" wrap gap={10}>
        <h3 className="">Check out your amazing progress!</h3>
        <Link
          to="/player/my-quests"
          style={{ textDecoration: 'none' }}
          aria-label="Navigate to My Quests page to view current objectives"
        >
          <Button intent="primary">Go to My Quests</Button>
        </Link>
      </Flex>
      <Flex className="my-award-container__details">
        <Flex
          justify="center"
          align="center"
          vertical
          gap={30}
          className="my-award-section"
        >
          <Flex vertical gap={10} className="my-award-section__leftbox">
            <Flex align="center" gap={10}>
              <h3 className="fw-bold">
                {renderBadgeTitle(currentBadgeLevel?.title)}
              </h3>
              <images.InfoCircleLg
                style={{ cursor: 'pointer' }}
                onClick={handleShowModal}
                role="button"
                tabIndex={0}
                aria-label="Show badge level information"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleShowModal()
                  }
                }}
              />
            </Flex>
            <Flex align="center" gap={10}>
              <h4>{stickerCount} Stickers</h4>
              <images.StarBadge aria-label="Star badge icon" />
            </Flex>
          </Flex>
        </Flex>
        <Flex
          justify="center"
          align="center"
          vertical
          className="my-award-progressbar"
        >
          <div
            className="award-progress-circle"
            role="progressbar"
            aria-label={`Badge progress: ${badgeStats.progressPercentage}% complete`}
          >
            <Progress
              type="circle"
              percent={badgeStats.progressPercentage}
              trailColor="rgba(207, 207, 207, 1)"
              strokeColor="rgba(86, 69, 218, 1)"
              strokeWidth={4}
              aria-label={`Progress towards next badge level: ${badgeStats.progressPercentage}%`}
            />
            <div className="award-progress-circle__progressbar">
              {currentBadgeLevel?.centerIcon}
              <Flex
                vertical
                align="center"
                className="award-progress-circle__progressbar-text"
              >
                <span className="progress-number">
                  {currentBadgeLevel?.description || 'Achieved'}
                </span>
              </Flex>
            </div>
          </div>
        </Flex>
        <Flex
          justify="center"
          align="center"
          vertical
          gap={30}
          className="my-award-section"
        >
          <Flex vertical gap={10} className="my-award-section__rightbox">
            <Flex align="center" gap={8}>
              <h5 className="fw-bold">Stickers earned today:</h5>
              <span
                className="my-award-section__rightbox-number"
                aria-label={`${badgeStats.earnedToday} stickers earned today`}
              >
                {badgeStats.earnedToday}
              </span>
            </Flex>
            <Flex align="center" gap={8}>
              <h5 className="fw-bold">Stickers earned this week:</h5>
              <span
                className="my-award-section__rightbox-number"
                aria-label={`${badgeStats.earnedThisWeek} stickers earned this week`}
              >
                {badgeStats.earnedThisWeek}
              </span>
            </Flex>
            <Flex align="center" gap={8}>
              <h5 className="fw-bold">Stickers to reach next level:</h5>
              <span
                className="my-award-section__rightbox-number"
                aria-label={`${badgeStats.stickersToNext} stickers needed for next level`}
              >
                {badgeStats.stickersToNext}
              </span>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Flex vertical gap={30} className="my-award-cards">
        <h3 className="my-award-cards__head">My Badges</h3>

        {/* Grid 1: Sticker Badges */}
        <div className="my-award-cards__content">
          {filteredBadges.stickerBadges.map((badge) =>
            renderBadge(badge, images.Sticker1000, images.StickerLocked)
          )}
        </div>

        {/* Grid 2: Skill Sets */}
        <div className="my-award-cards__content">
          {filteredBadges.skillSetBadges.map((badge) =>
            renderBadge(badge, images.CompleteSs1, images.LockedSs)
          )}
        </div>

        {/* Grid 3: Just the Facts (JTF) */}
        <div className="my-award-cards__content">
          {filteredBadges.jtfBadges.map((badge) =>
            renderBadge(badge, images.CompleteJTFNumPre, images.JtfLocked)
          )}
        </div>

        {/* Grid 4: Gym Add/Sub */}
        <div className="my-award-cards__content">
          {filteredBadges.gymAddSubBadges.map((badge) =>
            renderBadge(badge, images.CompleteGymNumAdd, images.GymLocked)
          )}
        </div>

        {/* Grid 5: Gym Mul/Div */}
        <div className="my-award-cards__content">
          {filteredBadges.gymMulDivBadges.map((badge) =>
            renderBadge(badge, images.CompleteGymDecAdd, images.GymLocked)
          )}
        </div>

        {/* Grid 6: Milestones and KAS */}
        <div className="my-award-cards__content">
          {filteredBadges.kasMilestoneBadges.map((badge) =>
            renderBadge(badge, images.Kas3Milestone3, images.KnsLocked)
          )}
        </div>

        {/* Grid 7: Games and Special Quests */}
        <div className="my-award-cards__content">
          {filteredBadges.mwGameBadges.map((badge) =>
            renderBadgeWithCount(
              badge,
              images.CompleteMwGame,
              images.MeasurementLocked
            )
          )}
        </div>

        {/* Grid 8: Remaining/Other Badges */}
        <div className="my-award-cards__content">
          {filteredBadges.bonusBadges.map((badge) =>
            renderBadgeWithCount(
              badge,
              images.CompleteBonusGame,
              images.BonusGameLocked
            )
          )}
        </div>

        {myBadges.questItems && myBadges.questItems.length === 0 && (
          <p>No badges earned yet.</p>
        )}
      </Flex>

      {/* Bonus Game */}
      <Flex vertical gap={30} className="my-award-cards">
        <h3 className="my-award-cards__head">Bonus Badges</h3>
        {/* Grid 1: Win Player of the Day */}
        {filteredBadges.potdBadges.length > 0 && (
          <Flex vertical gap={10}>
            <p className="fw-bold">Win Player of the Day</p>
            <div className="my-award-cards__content">
              {filteredBadges.potdBadges.map((badge) =>
                renderBadgeWithCount(badge, images.WinPotd, images.WinPotdLock)
              )}
            </div>
          </Flex>
        )}
        {/* Grid 2: Reach top “xxx” in your class */}
        {filteredBadges.topTenBadges.length > 0 && (
          <Flex vertical gap={10}>
            <p className="fw-bold">Reach top &quot;xxx&quot; in your class</p>
            <div className="my-award-cards__content">
              {filteredBadges.topTenBadges.map((badge) =>
                renderBadge(badge, images.TopTenSqure, images.LockedSs)
              )}
            </div>
          </Flex>
        )}{' '}
        {/* Grid 3: Earn 200 Stickers a Week */}
        {filteredBadges.weeklySticker200Badges.length > 0 && (
          <Flex vertical gap={10}>
            <p className="fw-bold">Earn 200 Stickers a Week</p>
            <div className="my-award-cards__content">
              {filteredBadges.weeklySticker200Badges.map((badge) =>
                renderBadgeWithCount(
                  badge,
                  images.Sticker200,
                  images.Sticker200Lock
                )
              )}
            </div>
          </Flex>
        )}
        {/* Grid 4: Complete a CT Game */}
        {filteredBadges.ctGameBadges.length > 0 && (
          <Flex vertical gap={10}>
            <p className="fw-bold">Complete a CT Game</p>
            <div className="my-award-cards__content">
              {filteredBadges.ctGameBadges.map((badge) =>
                renderBadge(badge, images.CompleteCtGame, images.CtGameLock)
              )}
            </div>
          </Flex>
        )}
        {/* Grid 5: try on epic */}
        {/* Grid 5: try on epic */}
        {filteredBadges.epicGameBadges.length > 0 && (
          <Flex vertical gap={10}>
            <p className="fw-bold">Try an Epic Game</p>
            <div className="my-award-cards__content">
              {filteredBadges.epicGameBadges.map((badge) =>
                renderBadge(badge, images.TryRps, images.TryRpsLock)
              )}
            </div>
          </Flex>
        )}{' '}
        {myBadges.questItems && myBadges.questItems.length === 0 && (
          <p>No badges earned yet.</p>
        )}
      </Flex>
      {/* Modal for Steps */}
      <Modal
        className="modal-steps"
        title={
          <Flex
            align="center"
            justify="space-between"
            className="modal-content"
          >
            <h5 className="fw-bold">Current Level</h5>
            <Flex gap={24}>
              <Flex gap={6} align="center">
                <div className="modal-badges light-green"></div>
                <span className="modal-badge-content">Completed</span>
              </Flex>
              <Flex gap={6} align="center">
                <div className="modal-badges purple"></div>
                <span className="modal-badge-content">Current</span>
              </Flex>
              <Flex gap={6} align="center">
                <div className="modal-badges grey"></div>
                <span className="modal-badge-content">Upcoming</span>
              </Flex>
            </Flex>
          </Flex>
        }
        open={isModalOpen}
        onOk={handleCloseModal}
        onCancel={handleCloseModal}
        closable={false}
        width="1200px"
        footer={[
          <Button key="close" onClick={handleCloseModal}>
            Okay
          </Button>
        ]}
      >
        <Steps
          current={getCurrentStepIndex(currentBadgeLevel)}
          labelPlacement="vertical"
          items={BADGE_ITEMS}
          className="custom-steps"
        />
      </Modal>
    </Flex>
  )
})

// Set display name for debugging and development tools
MyBadges.displayName = 'MyBadges'

export default MyBadges
